import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { handleApiError } from '@/utils/errorHandler';

export interface UseResetPasswordResult {
  // Form state
  password: string;
  setPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  showConfirmPassword: boolean;
  setShowConfirmPassword: (value: boolean) => void;
  
  // Validation errors
  passwordError: string;
  confirmPasswordError: string;
  
  // Loading state
  loading: boolean;
  
  // Error state
  error: string | null;
  
  // Email from URL params
  email: string;
  
  // Actions
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleGoToLogin: () => void;
}

/**
 * Hook xử lý logic đặt lại mật khẩu
 * - Lấy email từ URL params
 * - Validate password (tối thiểu 6 ký tự)
 * - Validate confirm password (phải khớp với password)
 * - Gọi API reset password
 * - Điều hướng về trang đăng nhập sau khi thành công
 */
export const useResetPassword = (): UseResetPasswordResult => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  /**
   * Validate password
   * - Kiểm tra password không rỗng
   * - Kiểm tra password có ít nhất 6 ký tự
   */
  const validatePassword = useCallback((passwordValue: string): boolean => {
    if (!passwordValue) {
      setPasswordError('Vui lòng nhập mật khẩu!');
      return false;
    }
    if (passwordValue.length < 6) {
      setPasswordError('Mật khẩu phải có ít nhất 6 ký tự!');
      return false;
    }
    setPasswordError('');
    return true;
  }, []);

  /**
   * Validate confirm password
   * - Kiểm tra confirm password không rỗng
   * - Kiểm tra confirm password khớp với password
   */
  const validateConfirmPassword = useCallback((confirmPasswordValue: string): boolean => {
    if (!confirmPasswordValue) {
      setConfirmPasswordError('Vui lòng xác nhận mật khẩu!');
      return false;
    }
    if (confirmPasswordValue !== password) {
      setConfirmPasswordError('Mật khẩu xác nhận không khớp!');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  }, [password]);

  /**
   * Xử lý thay đổi password
   * - Cập nhật password state
   * - Validate lại password nếu đã có lỗi
   * - Validate lại confirm password nếu đã nhập và có lỗi
   */
  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (passwordError) {
      validatePassword(newPassword);
    }
    if (confirmPassword && confirmPasswordError) {
      validateConfirmPassword(confirmPassword);
    }
  }, [passwordError, confirmPassword, confirmPasswordError, validatePassword, validateConfirmPassword]);

  /**
   * Xử lý thay đổi confirm password
   * - Cập nhật confirm password state
   * - Validate lại nếu đã có lỗi
   */
  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    if (confirmPasswordError) {
      validateConfirmPassword(newConfirmPassword);
    }
  }, [confirmPasswordError, validateConfirmPassword]);

  /**
   * Xử lý submit form đặt lại mật khẩu
   * - Validate email có trong URL params
   * - Validate password và confirm password
   * - Gọi API resetPasswordWithOTP
   * - Điều hướng về trang đăng nhập nếu thành công
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      message.error('Thiếu thông tin xác thực. Vui lòng quay lại trang quên mật khẩu.');
      navigate('/forgot-password');
      return;
    }

    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    
    if (!isPasswordValid || !isConfirmPasswordValid) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await authService.resetPasswordWithOTP(email, password);
      message.success('Đặt lại mật khẩu thành công!');
      setPassword('');
      setConfirmPassword('');
      navigate('/login');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Đặt lại mật khẩu thất bại. Vui lòng thử lại.';
      handleApiError(err, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, password, confirmPassword, validatePassword, validateConfirmPassword, navigate]);

  /**
   * Điều hướng về trang đăng nhập
   */
  const handleGoToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    passwordError,
    confirmPasswordError,
    loading,
    error,
    email,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    handleGoToLogin,
  };
};

